/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// invite-flow Wave 2 — shared RN+NativeWind ThreadPanel overlay v2.
//
// Wraps `ThreadOpenPanel` (in `packages/ui/src/threads/`) — the
// right-pane sidebar that renders the open thread. The shared overlay
// owns:
//   - the inner `useThreadViewModel(gateway)` hook,
//   - sync between the shell's `threadIdentifier` and the inner VM's
//     imperative `open`/`close`,
//   - the `closeOverlay('thread')` escalation when the inner VM closes
//     so the shell's view-model and the inner panel stay aligned.
//
// The actual visual surface (header / participant strip / message
// list / reply composer) lives in the legacy `threads/` directory and
// stays StyleSheet-based for now — the figma node hasn't been re-
// confirmed against NativeWind. A follow-up may port it.

import React, { useCallback, useEffect, useMemo } from "react";
import { View } from "react-native";
import {
	ThreadOpenPanel,
	type ThreadComposerProps,
	type ThreadRowActions,
} from "./ThreadOpenPanel";
import type { StagedAttachment } from "../chat/composer/AttachmentTray";
import { type ThreadGateway, type ThreadIdentifier } from "./types";
import { useThreadViewModel } from "./useThreadViewModel";
import { useColorScheme } from "../foundations/colorScheme";
import { tokens } from "../design-system/tokens";
import type { ChatRoomShellViewModel } from "../chat/useChatRoomShellViewModel";

const IS_WEB = typeof document !== "undefined" && typeof window !== "undefined";
// Docked thread panel width (Figma threads_flow 2804:99419 right pane).
const THREAD_PANEL_WIDTH = 400;

export interface ThreadPanelOverlayProps {
	readonly vm: ChatRoomShellViewModel;
	readonly gateway: ThreadGateway;
	/**
	 * Thread-scoped composer handlers (attachments/voice/GIF/sticker/poll/
	 * games/location) the host wires to send INTO the open thread. Built
	 * with the current thread root as `parent_id`.
	 */
	readonly composerProps?: ThreadComposerProps | undefined;
	/** Per-reply message-action handlers (react / menu / reactions). */
	readonly rowActions?: ThreadRowActions | undefined;
	/** Staged-attachment tray for the thread composer. */
	readonly stagedAttachments?: readonly StagedAttachment[] | undefined;
	readonly onRemoveStagedAttachment?: ((id: string) => void) | undefined;
	readonly onFlushStagedAttachments?: (() => Promise<void> | void) | undefined;
	/**
	 * When true, the panel renders IN-FLOW filling its parent (the host
	 * supplies the docked, resizable column) instead of as a fixed-width
	 * drawer pinned to the viewport edge. Used by the unified right-panel
	 * layout (Threads/Notes/Tasks share one resizable slot).
	 */
	readonly embedded?: boolean | undefined;
}

export const ThreadPanelOverlay: React.FC<ThreadPanelOverlayProps> = ({
	vm,
	gateway,
	composerProps,
	rowActions,
	stagedAttachments,
	onRemoveStagedAttachment,
	onFlushStagedAttachments,
	embedded = false,
}) => {
	const threadVm = useThreadViewModel({ gateway });
	// Destructure the imperative methods. Both are `useCallback([], [])`
	// inside the hook → stable across renders, so the sync effect
	// below can list them as dependencies without re-firing every
	// render the way `[threadVm]` would.
	const { open: openThread, close: closeThread } = threadVm;

	const identifier: ThreadIdentifier | null = vm.state.threadIdentifier;

	// Sync the inner thread VM with the shell's active identifier.
	// Depending on the full `threadVm` object would cause
	// "Maximum update depth exceeded" because `state` is part of the
	// vm shape and changes on every snapshot push — each change
	// re-fires this effect, which calls `open()`, which sets state
	// again. Depending on the stable callbacks only means the effect
	// fires exactly when `identifier` changes, which is the intent.
	useEffect(() => {
		if (identifier === null) {
			closeThread();
			return;
		}
		openThread(identifier);
	}, [identifier, openThread, closeThread]);

	const wrappedClose = useCallback(() => {
		closeThread();
		vm.handlers.closeOverlay("thread");
	}, [closeThread, vm.handlers]);

	const wrappedVm = useMemo(
		() => ({ ...threadVm, close: wrappedClose }),
		[threadVm, wrappedClose],
	);

	const scheme = useColorScheme();
	const colors = tokens.color.semantic[scheme === "dark" ? "dark" : "light"];

	if (identifier === null) {
		return null;
	}

	// The panel needs a layout slot of its own — the host mounts this
	// overlay as a bare sibling outside the chat flex-row, and
	// `ThreadOpenPanel` is `flex:1/width:100%` with no positioning, so
	// without a wrapper it has nowhere to render. Dock it to the right
	// edge as a fixed-width drawer (Figma threads_flow right pane). On
	// web a raw fixed `<div>` is the reliable way to pin to the viewport
	// (RN-Web can normalize `position:'fixed'` off a View); native uses
	// an absolutely-positioned View against the app root.
	const panel = (
		<ThreadOpenPanel
			vm={wrappedVm}
			focused
			{...(composerProps !== undefined ? { composerProps } : {})}
			{...(rowActions !== undefined ? { rowActions } : {})}
			{...(stagedAttachments !== undefined ? { stagedAttachments } : {})}
			{...(onRemoveStagedAttachment !== undefined ? { onRemoveStagedAttachment } : {})}
			{...(onFlushStagedAttachments !== undefined ? { onFlushStagedAttachments } : {})}
		/>
	);

	// Embedded: fill the host-provided resizable column. No fixed/absolute
	// positioning, no own border/shadow — the SidePanelFrame owns those.
	if (embedded) {
		return (
			<View className="flex-1 min-h-0 h-full w-full">{panel}</View>
		);
	}

	if (IS_WEB) {
		return React.createElement(
			"div",
			{
				style: {
					position: "fixed",
					top: 0,
					right: 0,
					bottom: 0,
					width: `min(${THREAD_PANEL_WIDTH}px, 100vw)`,
					display: "flex",
					flexDirection: "column",
					zIndex: 60,
					backgroundColor: colors.surface.primary,
					borderLeft: `1px solid ${colors.border.secondary}`,
					boxShadow: "-8px 0 24px rgba(0,0,0,0.18)",
				},
			},
			panel,
		);
	}

	return (
		<View
			style={{
				position: "absolute",
				top: 0,
				right: 0,
				bottom: 0,
				width: THREAD_PANEL_WIDTH,
				zIndex: 60,
				backgroundColor: colors.surface.primary,
				borderLeftWidth: 1,
				borderLeftColor: colors.border.secondary,
			}}
		>
			{panel}
		</View>
	);
};
